import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren } from 'react';

import { graphql, type HeadFC } from 'gatsby';
import {
  LucidHead,
  Container,
  Heading,
  Text,
  Link,
  Box,
  ImageWithPlaceholder,
  VideoPlayer,
  Image,
  StatGrid,
  LogoSection,
  QuoteCardCarousel,
  ResourceCard,
  PreFooterCta,
  FeatureGrid,
} from '@lucid/components';
import { withImage, useSmartlingStrings } from '@lucid/hooks';
import { isDefined } from '@lucid/utils';

import type { Lucid } from 'types/types';

const CoEnterprise2: FC<PropsWithChildren<Lucid.Context.PageProps>> = ({ data, pageContext }) => {
  const { directus } = data;
  const node = directus.node;
  const { nodeTranslations } = node;
  const translatedData = node.translations?.[0] as Lucid.Directus.Generated.Co_Enterprise_2_Translations;
  const { shortCode, urlLangPrefix, localeId, pageType, nid, shouldHideSsr, metatags, product } = pageContext;
  const {
    hero,
    video,
    product_section_heading,
    product_section_text,
    product_section_image,
    stat_section_heading,
    stat_blocks,
    stat_section_text,
    stat_section_link,
    logo_section,
    feature_grid_items,
    feature_grid_heading,
    feature_grid_text,
    feature_highlight_section_heading,
    feature_highlight_section_text,
    feature_highlight_section_image,
    feature_highlight_section_items,
    upper_card_section_heading,
    upper_card_section_text,
    upper_cards,
    lower_card_section_heading,
    lower_card_section_text,
    lower_cards,
    lower_stat_section_heading,
    lower_stat_section_text,
    lower_stat_blocks,
    lower_stat_section_note,
    lower_stat_section_link,
    pre_footer_heading,
    pre_footer_link,
    quote_carousel_set,
    quote_carousel_section_heading,
  } = translatedData;

  const withSmartling = useSmartlingStrings({
    keys: ['mktg_homepage_learn_more_about', 'mktg_read_more_about'],
    shortCode,
  });

  const statBlocks = stat_blocks?.map(block => block.stat_block_id).filter(isDefined);
  const lowerStatBlocks = lower_stat_blocks?.map(card => card.stat_block_id).filter(isDefined);
  const featureGridItems = feature_grid_items?.map(fgi => fgi.feature_grid_item_id).filter(isDefined) || [];
  const quoteCarouselItems = quote_carousel_set?.map(qci => qci.quote_carousel_item_id).filter(isDefined);
  const logoList = logo_section?.logo_section?.logo_list
    ?.map(logo => logo.external_logo_id?.name)
    .filter(isDefined) as string[];
  const hasStatsOrLogos = !!statBlocks?.length || !!logoList?.length;

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      {hero && (
        <Box
          css={theme => ({
            margin: `${theme.space.s104}px auto`,
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            padding: '0 60px',
            maxWidth: '90rem',
          })}
        >
          <Box css={theme => ({ gridRow: '1 / 2', gridColumn: '1 / 3', zIndex: theme.zIndices.z100, width: '100%' })}>
            <Heading
              as="h1"
              css={theme => ({
                maxWidth: 'auto',
                [theme.breakpoints.bp768.min]: {
                  maxWidth: '680px',
                },
                [theme.breakpoints.bp1220.max]: {
                  maxWidth: '880px',
                },
                [theme.breakpoints.bp1220.min]: {
                  maxWidth: '1080px',
                },
              })}
              highlightTextColor="blue.60"
              text={hero.heading}
              variant="hero7XL700"
            />
            <Text css={{ maxWidth: '640px' }} variant="paragraphXL300">
              {hero.text}
            </Text>
            {hero?.link_set?.[0]?.links_id?.final_url && (
              <Link displayAsButton href={hero.link_set?.[0]?.links_id.final_url} variant="primary">
                {hero.link_set?.[0]?.links_id.default_link_text}
              </Link>
            )}
          </Box>
          <Box
            css={theme => ({
              gridRow: '1 / 2',
              gridColumn: '2 / 3',
              display: 'none',
              [theme.breakpoints.bp768.min]: {
                display: 'block',
              },
            })}
          >
            <Image imageAlt={hero.image?.translations?.[0]?.alt || ''} imageSrc={withImage(hero.image)} />
          </Box>
        </Box>
      )}
      {video?.external_url && (
        <Box css={theme => ({ backgroundColor: theme.colors.coolGrey[5], padding: `${theme.space.s80}px 0` })}>
          <Container>
            <VideoPlayer
              controls
              css={theme => ({
                flex: '0 1 100%',
                margin: 'auto',
                marginBottom: theme.space.s32,
                borderRadius: theme.radii.r16,
                [theme.breakpoints.bp900.min]: {
                  flex: '0 1 65%',
                  marginBottom: theme.space.s0,
                },
              })}
              description={video?.alt_text}
              descriptionElementId="video-section-description"
              ratio="wide"
              url={video?.external_url}
            />
          </Container>
        </Box>
      )}
      {product_section_heading && (
        <Container
          css={theme => ({
            margin: `${theme.space.s104}px auto`,
          })}
        >
          <Box css={{ maxWidth: '740px' }}>
            <Box
              css={theme => ({
                display: 'flex',
                paddingBottom: theme.space.s40,
                gap: '16px',
                marginLeft: '-12px',
                flexDirection: 'column',
                [theme.breakpoints.bp768.min]: { flexDirection: 'row' },
              })}
            >
              {/* NOTE: These are hardcoded because these links will always be these products */}
              <Link
                aria-label={withSmartling('mktg_homepage_learn_more_about', {
                  title: 'Lucidchart',
                })}
                css={theme => ({
                  padding: '12px 0 8px 12px',
                  border: '1px solid transparent',
                  '&:hover, &:focus, &:active': {
                    border: `1px solid ${theme.colors.coolGrey[60]}`,
                  },
                  borderRadius: theme.radii.r4,
                })}
                href="https://www.lucidchart.com/pages/product"
              >
                <Image
                  css={{ maxWidth: '168px' }}
                  imageAlt=""
                  imageSrc="https://corporate-assets.lucid.co/co/120f906d-9405-4960-a68b-ff08a452835a.svg"
                />
              </Link>
              <Link
                aria-label={withSmartling('mktg_homepage_learn_more_about', {
                  title: 'Lucidspark',
                })}
                css={theme => ({
                  padding: '12px 0 8px 12px',
                  border: '1px solid transparent',
                  '&:hover, &:focus, &:active': {
                    border: `1px solid ${theme.colors.coolGrey[60]}`,
                  },
                  borderRadius: theme.radii.r4,
                })}
                href="https://lucidspark.com/product"
              >
                <Image
                  css={{ maxWidth: '168px' }}
                  imageAlt=""
                  imageSrc="https://corporate-assets.lucid.co/co/9af2902e-a123-4e64-abd0-9326a6db3a29.svg"
                />
              </Link>
            </Box>
            <Heading as="h2" variant="hero2XL700">
              {product_section_heading}
            </Heading>
            <Text variant="paragraphL400">{product_section_text}</Text>
          </Box>
          <ImageWithPlaceholder alt="" src={withImage(product_section_image)} />
        </Container>
      )}
      {hasStatsOrLogos && (
        <Box css={theme => ({ backgroundColor: theme.colors.coolGrey[5] })}>
          {!!statBlocks?.length && (
            <StatGrid
              heading={stat_section_heading}
              headingHighlightColor="pink.70"
              items={statBlocks}
              languageKey={shortCode}
              link={stat_section_link}
              statColor="pink.70"
              text={stat_section_text}
              variant="divided"
            />
          )}
          {!!logoList?.length && (
            <Box
              css={theme => [
                {
                  width: '100%',
                  overflow: 'hidden',
                  [theme.breakpoints.bp900.min]: {
                    marginTop: theme.space.s64,
                  },
                },
              ]}
            >
              <LogoSection height={40} logos={logoList} logosColor="coolGrey" variant="carousel" />
            </Box>
          )}
        </Box>
      )}
      {!!upper_cards?.length && (
        <Container css={theme => ({ paddingTop: theme.space.s120, paddingBottom: theme.space.s120 })}>
          <Box css={{ maxWidth: '740px' }}>
            <Heading as="h2" highlightTextColor="indigo.60" text={upper_card_section_heading} variant="heading2XL" />
            <Text variant="paragraphL400">{upper_card_section_text}</Text>
          </Box>
          <Box
            css={theme => ({
              display: 'flex',
              gap: theme.space.s40,
              flexWrap: 'wrap',
              justifyContent: 'center',
              [theme.breakpoints.bp768.min]: {
                justifyContent: 'start',
              },
            })}
          >
            {upper_cards.map(card => {
              const cardData = card.basic_card_id;

              return (
                <ResourceCard
                  key={`upper-resourceCard-${card.id || cardData?.id}`}
                  heading={cardData?.heading || ''}
                  link={{
                    final_url: cardData?.link?.final_url,
                    default_link_text: cardData?.link?.default_link_text,
                    shouldLinkWholeCard: false,
                    accessibleDescription: `${cardData?.link?.default_link_text} - ${cardData?.heading}`,
                  }}
                  media={{ image: cardData?.image }}
                  text={{ value: cardData?.text }}
                  variant="gradient"
                />
              );
            })}
          </Box>
        </Container>
      )}
      {feature_highlight_section_heading && (
        <Box
          css={theme => ({
            backgroundColor: theme.colors.indigo[80],
            paddingTop: theme.space.s64,
            paddingBottom: theme.space.s64,
          })}
        >
          <Container>
            <Box css={{ maxWidth: '740px' }}>
              <Heading
                as="h2"
                css={theme => ({
                  color: theme.colors.white,
                  width: '100%',
                })}
                highlightTextColor="teal.20"
                text={feature_highlight_section_heading}
                variant="hero2XL700"
              />
              <Text css={theme => ({ color: theme.colors.white })} variant="paragraphL400">
                {feature_highlight_section_text}
              </Text>
            </Box>
            <Box
              css={theme => ({
                display: 'grid',
                gridTemplateAreas: '"image" "text"',
                justifyItems: 'center',
                [theme.breakpoints.bp768.min]: {
                  gridTemplateAreas: '"image text"',
                  gridTemplateColumns: '375px 1fr',
                  columnGap: theme.space.s80,
                },
                [theme.breakpoints.bp900.min]: {
                  gridTemplateAreas: '"image text"',
                  gridTemplateColumns: '510px 1fr',
                  columnGap: theme.space.s80,
                },
              })}
            >
              <ImageWithPlaceholder
                alt={feature_highlight_section_image?.translations?.[0]?.alt || ''}
                aspectRatio="3:4"
                css={theme => ({
                  maxWidth: '510px',
                  gridArea: 'image',
                  display: 'none',
                  [theme.breakpoints.bp768.min]: { display: 'block' },
                })}
                src={withImage(feature_highlight_section_image)}
              />
              <Box css={{ gridArea: 'text' }}>
                {feature_highlight_section_items?.map((item, index: number) => {
                  const itemData = item?.resource_card_id;
                  const isNotLastItem = feature_highlight_section_items?.length !== index + 1;

                  return (
                    <React.Fragment key={`enterprise-section-item-${itemData?.id}`}>
                      <Text
                        css={theme => ({
                          color: 'white',
                          marginBottom: theme.space.s24,
                          '.colorizedText': {
                            fontWeight: theme.fontWeights.fw700,
                          },
                        })}
                        highlightTextColor="teal.20"
                        text={`{{${itemData?.heading}}} ${itemData?.text}`}
                        variant="paragraphL400"
                      />
                      <Link
                        aria-label={`${itemData?.link?.default_link_text} - ${itemData?.heading}`}
                        css={theme => ({
                          fontSize: theme.fontSizes.fs20,
                        })}
                        href={itemData?.link?.final_url}
                        iconAnimation="navigational"
                        variant="primaryInverse"
                      >
                        {itemData?.link?.default_link_text}
                      </Link>
                      {isNotLastItem && (
                        <Box
                          css={theme => ({
                            height: '1px',
                            borderBottom: '1px solid white',
                            margin: `${theme.space.s40}px 0`,
                          })}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          </Container>
        </Box>
      )}
      {!!featureGridItems.length && (
        <Box
          css={theme => ({
            backgroundColor: theme.colors.indigo[80],
            padding: `${theme.space.s104}px 0px`,
          })}
        >
          <Container>
            <FeatureGrid
              hasDarkBackground
              items={featureGridItems}
              sectionSubtitle={feature_grid_text}
              sectionTitle={feature_grid_heading}
              titleColor="white"
              titleTextVariant="headingXL"
              variant="resources"
            />
          </Container>
        </Box>
      )}
      {!!quoteCarouselItems?.length && (
        <Box
          css={theme => ({
            background: `linear-gradient(180deg, ${theme.colors.indigo[10]} 0%, ${theme.colors.white} 100%)`,
            padding: `${theme.space.s104}px 0`,
          })}
        >
          <Container>
            <Heading as="h2" variant="heading2XL">
              {quote_carousel_section_heading}
            </Heading>
            <QuoteCardCarousel css={{ display: 'flex' }} quotes={quoteCarouselItems} />
          </Container>
        </Box>
      )}
      {!!lower_cards?.length && (
        <Container css={theme => ({ paddingTop: theme.space.s120, paddingBottom: theme.space.s120 })}>
          <Box css={{ maxWidth: '740px' }}>
            <Heading as="h2" highlightTextColor="indigo.60" text={lower_card_section_heading} variant="heading2XL" />
            <Text variant="paragraphL400">{lower_card_section_text}</Text>
          </Box>
          <Box
            css={theme => ({
              display: 'flex',
              gap: theme.space.s40,
              flexWrap: 'wrap',
              justifyContent: 'center',
              [theme.breakpoints.bp768.min]: {
                justifyContent: 'start',
              },
            })}
          >
            {lower_cards.map(card => {
              const cardData = card.basic_card_id;

              return (
                <ResourceCard
                  key={`lower-resourceCard-${card.id || cardData?.id}`}
                  heading={cardData?.heading || ''}
                  link={{
                    final_url: cardData?.link?.final_url,
                    default_link_text: cardData?.link?.default_link_text,
                    shouldLinkWholeCard: false,
                    accessibleDescription: `${cardData?.link?.default_link_text} - ${cardData?.heading}`,
                  }}
                  media={{ image: cardData?.image }}
                  text={{ value: cardData?.text }}
                  variant="gradient"
                />
              );
            })}
          </Box>
        </Container>
      )}
      {!!lowerStatBlocks?.length && (
        <StatGrid
          css={theme => ({
            background: `linear-gradient(140.24deg, ${theme.colors.coolGrey[100]} 5.93%, ${theme.colors.blue[100]} 78.67%)`,
          })}
          heading={lower_stat_section_heading}
          headingHighlightColor="purple.20"
          items={lowerStatBlocks}
          languageKey={shortCode}
          link={lower_stat_section_link}
          note={lower_stat_section_note}
          statColor="blue.60"
          text={lower_stat_section_text}
          variant="cards"
        />
      )}
      {pre_footer_link && (
        <PreFooterCta
          backgroundColor="coolGrey.100"
          ctas={[pre_footer_link]}
          data-ab="pre-footer-cta-section_container"
          heading={pre_footer_heading}
          headingVariant="hero4XL400"
          variant="secondary"
        />
      )}
    </Layout>
  );
};

export default CoEnterprise2;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query CoEnterprise2Query($nid: ID!, $shortCode: String!) {
    directus {
      node: co_enterprise_2_by_id(id: $nid) {
        nodeTranslations: translations {
          is_published
          languages_code {
            short_code
          }
          path {
            url
          }
        }
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          hero {
            id
            heading
            text
            link_set {
              links_id {
                id
                final_url
                default_link_text
              }
            }
            image {
              ...mediaFragment
            }
          }
          video {
            title
            external_url
            alt_text
            preview_image {
              ...mediaFragment
            }
            translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
              title
              alt_description
            }
          }
          product_section_heading
          product_section_text
          product_section_image {
            ...mediaFragment
          }
          stat_section_heading
          stat_section_text
          stat_section_link {
            id
            final_url
            default_link_text
          }
          stat_blocks {
            stat_block_id {
              id
              heading
              text
            }
          }
          logo_section {
            heading
            link {
              ...linkFragment
            }
            logo_section {
              logo_list {
                external_logo_id {
                  id
                  name
                }
              }
            }
          }
          upper_card_section_heading
          upper_card_section_text
          upper_cards {
            basic_card_id {
              id
              heading
              text
              image {
                ...mediaFragment
              }
              link {
                ...linkFragment
              }
            }
          }
          feature_grid_heading
          feature_grid_text
          feature_grid_items {
            feature_grid_item_id {
              id
              heading
              html
              image {
                ...mediaFragment
              }
              link {
                ...linkFragment
              }
            }
          }
          quote_carousel_section_heading
          quote_carousel_set {
            quote_carousel_item_id {
              id
              quote {
                id
                full_name
                company
                company_logo {
                  ...mediaFragment
                }
                translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
                  position
                  text
                }
              }
              link {
                ...linkFragment
              }
            }
          }
          feature_highlight_section_heading
          feature_highlight_section_text
          feature_highlight_section_image {
            ...mediaFragment
          }
          feature_highlight_section_items {
            resource_card_id {
              id
              heading
              text
              link {
                ...linkFragment
              }
            }
          }
          lower_card_section_heading
          lower_card_section_text
          lower_cards {
            basic_card_id {
              id
              heading
              text
              image {
                ...mediaFragment
              }
              link {
                ...linkFragment
              }
            }
          }
          lower_stat_section_heading
          lower_stat_section_text
          lower_stat_section_note
          lower_stat_section_link {
            id
            final_url
            default_link_text
          }
          lower_stat_blocks {
            stat_block_id {
              id
              heading
              text
              image {
                ...mediaFragment
              }
            }
          }
          pre_footer_heading
          pre_footer_link {
            id
            beacon_id
            final_url
            default_link_text
          }
        }
      }
    }
  }
`;
